import {
  FloatingFormItem,
  InputDecimal,
  InputText,
  PlanAccountsSelect,
} from '@/components/_common';
import { BankAccount } from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import {
  BankAccountSettingsFormValues,
  PaymentDateType,
  PaymentDateTypeLabel,
} from '@/core/domain/enterprise/BankAccountsSettings/BankAccountsSettings.types';
import { bankConfig } from '@/core/domain/financial/Boletos/Boletos.dictionary';
import { useDefaultEmptyFieldError } from '@/hooks';
import {
  Checkbox,
  Col,
  Divider,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import AgreementInstantRegisterFields from './AgreementInstantRegisterFields';
import WalletFields from './WalletFields';
import ConfigBankSlipBasicFields from './ConfigBankSlipBasicFields';
import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import PaymentTypeCaixaSelect from './PaymentTypeCaixaSelect';

interface Props {
  form: FormInstance<BankAccountSettingsFormValues>;
  bankAccount?: BankAccount;
}

const ConfigBankSlipForm: React.FC<Props> = ({ form, bankAccount }) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const bankMapFields = bankAccount ? bankConfig[bankAccount?.bank.code] : null;

  const agreementInstantRegister = Form.useWatch(
    ['general', 'agreementInstantRegister'],
    form,
  );

  const agreementBankOurNumber = Form.useWatch(
    ['general', 'agreementBankOurNumber'],
    form,
  );

  const hasBankFee = Form.useWatch(['general', 'hasBankFee'], form);

  return (
    <Form form={form} name="bankAccountConfigBullet">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Row gutter={[12, 12]}>
            <Tooltip
              trigger="click"
              title={
                !!bankMapFields?.fields.agreementNumber?.helpMessage
                  ? bankMapFields?.fields.agreementNumber?.helpMessage
                  : 'Neste campo pode ser informado o número do convênio ou número da conta sem o DV (seguir instruções do banco).'
              }
            >
              {!!bankMapFields?.fields.agreementNumber && (
                <Col span={24} md={12} lg={6} xxl={3}>
                  <FloatingFormItem
                    label="Número Convênio"
                    name={['general', 'agreementNumber']}
                    rules={
                      bankMapFields?.fields.agreementNumber.required
                        ? [defaultEmptyFieldError]
                        : []
                    }
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>
              )}
            </Tooltip>

            <Tooltip trigger="click" title="Pode ser informado um texto livre.">
              {!!bankMapFields?.fields.agreementDescription && (
                <Col span={24} md={12} lg={6} xxl={7}>
                  <FloatingFormItem
                    label="Descrição Convênio"
                    name={['general', 'agreementDescription']}
                    rules={[defaultEmptyFieldError]}
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>
              )}
            </Tooltip>

            <WalletFields bankAccount={bankAccount} />

            <Tooltip trigger="click" title="Espécie do Título Ex: R$">
              {!!bankMapFields?.fields.agreementSpecies && (
                <Col span={24} md={12} lg={6} xxl={3}>
                  <FloatingFormItem
                    label="Espécie"
                    name={['general', 'agreementSpecies']}
                    initialValue={'R$'}
                  >
                    <Input disabled />
                  </FloatingFormItem>
                </Col>
              )}
            </Tooltip>

            <PaymentTypeCaixaSelect bankCode={bankAccount?.bank.code} />

            <Tooltip trigger="click" title="Layout adotado pelo Banco">
              {!!bankMapFields?.fields.agreementCnabPattern && (
                <Col span={24} md={12} lg={6} xxl={4}>
                  <FloatingFormItem
                    label="Padrão CNAB"
                    name={['general', 'agreementCnabPattern']}
                    rules={
                      bankMapFields?.fields.agreementCnabPattern.required
                        ? [defaultEmptyFieldError]
                        : []
                    }
                  >
                    <Select
                      style={{ width: '100' }}
                      options={bankMapFields?.fields.agreementCnabPattern.options?.map(
                        (value) => ({ value, label: value }),
                      )}
                    />
                  </FloatingFormItem>
                </Col>
              )}
            </Tooltip>

            {!!bankMapFields?.fields.agreementRestartDaily && (
              <>
                {bankMapFields?.fields.agreementRestartDaily.visible && (
                  <Col span={24} md={12} lg={6} xxl={4}>
                    <FloatingFormItem
                      label="Reiniciar número da remessa"
                      name={['general', 'agreementRestartDaily']}
                      initialValue={
                        bankMapFields?.fields.agreementRestartDaily.initialValue
                      }
                    >
                      <Select
                        disabled={
                          bankMapFields?.fields.agreementRestartDaily.disabled
                        }
                        style={{ width: '100' }}
                        options={[
                          {
                            value: 1,
                            label: 'Sim',
                          },
                          {
                            value: 0,
                            label: 'Não',
                          },
                        ]}
                        allowClear
                      />
                    </FloatingFormItem>
                  </Col>
                )}
              </>
            )}

            <Tooltip trigger="click" title="Número atual da remessa">
              {!!bankMapFields?.fields.agreementRemittanceNumber && (
                <Col span={24} md={12} lg={6} xxl={4}>
                  <FloatingFormItem
                    label="Número atual"
                    name={['general', 'agreementRemittanceNumber']}
                    rules={
                      bankMapFields?.fields.agreementRemittanceNumber.required
                        ? [defaultEmptyFieldError]
                        : []
                    }
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>
              )}
            </Tooltip>

            {!!bankMapFields?.fields.agreementBankOurNumber && (
              <Col span={24} md={12} lg={6} xxl={4}>
                <FloatingFormItem
                  label="Nosso Numero controlado pelo banco"
                  name={['general', 'agreementBankOurNumber']}
                  initialValue={
                    bankMapFields?.fields.agreementBankOurNumber.initialValue
                  }
                  rules={[defaultEmptyFieldError]}
                >
                  <Select
                    disabled={
                      bankMapFields?.fields.agreementBankOurNumber.disabled
                    }
                    style={{ width: '100%' }}
                    options={[
                      {
                        value: 1,
                        label: 'Sim',
                      },
                      {
                        value: 0,
                        label: 'Não',
                      },
                    ]}
                    allowClear
                  />
                </FloatingFormItem>
              </Col>
            )}

            {!Boolean(agreementBankOurNumber) && (
              <Tooltip trigger="click" title="Nosso Número atual do boleto">
                <Col span={24} md={12} lg={6} xxl={4}>
                  <FloatingFormItem
                    label="Nosso número atual"
                    name={['general', 'agreementCurrentOurNumber']}
                    rules={
                      !Boolean(agreementBankOurNumber)
                        ? [defaultEmptyFieldError]
                        : []
                    }
                  >
                    <InputText onlyNumber />
                  </FloatingFormItem>
                </Col>
              </Tooltip>
            )}

            {!!bankMapFields?.fields.agreementLayoutFileVersion && (
              <Col span={24} md={12} lg={6} xxl={4}>
                <FloatingFormItem
                  label="Versão Layout"
                  name={['general', 'agreementLayoutFileVersion']}
                >
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    options={bankMapFields?.fields.agreementLayoutFileVersion.options?.map(
                      (value) => ({ value, label: value }),
                    )}
                  />
                </FloatingFormItem>
              </Col>
            )}

            {!!bankMapFields?.fields.agreementRemittanceDensity && (
              <Col span={24} md={12} lg={6} xxl={4}>
                <FloatingFormItem
                  label="Código da densidade da Remessa"
                  name={['general', 'agreementRemittanceDensity']}
                >
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    options={bankMapFields?.fields.agreementRemittanceDensity.options?.map(
                      (value) => ({ value, label: value }),
                    )}
                  />
                </FloatingFormItem>
              </Col>
            )}

            <Tooltip trigger="click" title="Tipo da carteira de cobrança.">
              {!!bankMapFields?.fields.agreementWalletCode && (
                <Col span={24} md={12} lg={6} xxl={4}>
                  <FloatingFormItem
                    label="Código carteira"
                    name={['general', 'agreementWalletCode']}
                    rules={
                      !!bankMapFields
                        ? [
                            {
                              type: 'string',
                              max: bankMapFields?.fields.agreementWalletCode
                                ?.maxLength,
                              message: `Quantidade máxima: ${bankMapFields?.fields.agreementWalletCode?.maxLength}`,
                            },
                          ]
                        : [defaultEmptyFieldError]
                    }
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>
              )}
            </Tooltip>

            <Tooltip
              trigger="click"
              title="Geralmente o valor a ser preenchido neste campo 
                         é o mesmo que o informado no Código do beneficiário. 
                         Porém, recomendamos a confirmação do valor junto à instituição bancária."
            >
              {!!bankMapFields?.fields.agreementContractNumber && (
                <Col span={24} md={12} lg={6} xxl={4}>
                  <FloatingFormItem
                    label="Número contrato"
                    name={['general', 'agreementContractNumber']}
                  >
                    <Input />
                  </FloatingFormItem>
                </Col>
              )}
            </Tooltip>

            {!!bankMapFields?.fields.titleCodeReferente?.visible && (
              <Col span={24} md={12} lg={6} xxl={3}>
                <FloatingFormItem
                  label="Código referência"
                  name={['general', 'titleCodeReference']}
                >
                  <Input
                    maxLength={
                      bankMapFields?.fields.titleCodeReferente.maxLength
                    }
                  />
                </FloatingFormItem>
              </Col>
            )}

            <WalletFields bankAccount={bankAccount} />

            {/* Apenas para os seguintes bancos será possível alterar para false: Caixa(104), Sicoob(756), Bradesco(237), Itaú(341) */}
            {!!bankMapFields?.fields.agreementWsConsult?.visible && (
              <Col span={24} md={12} lg={6} xxl={4}>
                <FloatingFormItem
                  label="Convênio consulta ws"
                  name={['general', 'agreementWsConsult']}
                  initialValue={
                    bankMapFields?.fields.agreementWsConsult.initialValue
                  }
                >
                  <Select
                    disabled={bankMapFields?.fields.agreementWsConsult.disabled}
                    style={{ width: '100' }}
                    options={[
                      {
                        value: 1,
                        label: 'Sim',
                      },
                      {
                        value: 0,
                        label: 'Não',
                      },
                    ]}
                    allowClear
                  />
                </FloatingFormItem>
              </Col>
            )}

            <ConfigBankSlipBasicFields bankAccount={bankAccount} />

            <Tooltip
              trigger="click"
              title="Define se o convênio irá ou não utilizar a transmissão instantânea."
            >
              {!!bankMapFields?.fields.agreementInstantRegister && (
                <Col span={24} md={12} lg={6} xxl={4}>
                  <FloatingFormItem
                    label="Registro Instantâneo"
                    name={['general', 'agreementInstantRegister']}
                    initialValue={
                      bankMapFields?.fields.agreementInstantRegister
                        .initialValue
                    }
                  >
                    <Select
                      disabled={
                        bankMapFields?.fields.agreementInstantRegister.disabled
                      }
                      style={{ width: '100%' }}
                      options={[
                        {
                          value: 1,
                          label: 'Sim',
                        },
                        {
                          value: 0,
                          label: 'Não',
                        },
                      ]}
                      allowClear
                    />
                  </FloatingFormItem>
                </Col>
              )}
            </Tooltip>

            {bankMapFields?.fields.agreementSandboxEnvironment?.visible && (
              <Col span={24} md={12} lg={6} xxl={4}>
                <FloatingFormItem
                  label="Ambiente"
                  name={['general', 'agreementSandboxEnvironment']}
                  initialValue={
                    bankMapFields?.fields.agreementSandboxEnvironment
                      .initialValue
                  }
                  rules={[defaultEmptyFieldError]}
                >
                  <Select
                    style={{ width: '100%' }}
                    options={[
                      {
                        value: 1,
                        label: 'Sandbox',
                      },
                      {
                        value: 0,
                        label: 'Produção',
                      },
                    ]}
                  />
                </FloatingFormItem>
              </Col>
            )}

            {!!agreementInstantRegister && (
              <AgreementInstantRegisterFields bankAccount={bankAccount} />
            )}

            <Divider />

            <Col span={24}>
              <Typography.Title level={4}>
                Taxa para emissões de boletos
              </Typography.Title>
            </Col>

            <Col span={24}>
              <FloatingFormItem
                valuePropName="checked"
                name={['general', 'hasBankFee']}
              >
                <Checkbox>Tem taxa bancária</Checkbox>
              </FloatingFormItem>
            </Col>

            {hasBankFee && (
              <>
                <Col span={24} md={12} lg={6} xxl={4}>
                  <FloatingFormItem
                    label="Taxa bancária"
                    name={['general', 'bankFee']}
                  >
                    <InputDecimal />
                  </FloatingFormItem>
                </Col>

                <Col span={24} md={12} lg={6} xxl={4}>
                  <FloatingFormItem
                    label="Plano de Contas"
                    name={['general', 'planAccountId']}
                    rules={[defaultEmptyFieldError]}
                  >
                    <PlanAccountsSelect
                      queryFilter={{
                        hierarchy: PlanAccountHierarchy.Analitical,
                        destination: PlanAccountDestination.Expense,
                        active: true,
                      }}
                    />
                  </FloatingFormItem>
                </Col>
              </>
            )}

            <Col span={24}>
              <Divider />

              <Typography.Title level={4}>
                Data de pagamento dos lançamentos
              </Typography.Title>

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  label="Data de pagamento determinada por:"
                  name={['general', 'paymentDateType']}
                  rules={[defaultEmptyFieldError]}
                  initialValue={PaymentDateType.draweePaysTheBill}
                >
                  <Select
                    style={{ width: '100%' }}
                    options={Object.values(PaymentDateType).map((value) => ({
                      value,
                      label: PaymentDateTypeLabel[value],
                    }))}
                  />
                </FloatingFormItem>
              </Col>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default ConfigBankSlipForm;
